import { useEffect, useState, useCallback } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { JiraProject, JiraProjects } from '../__types__/apiUtils';

import { fetchJiraProjects, fetchRecentJiraProjects } from './apiUtils';

export type UseGetJiraProjectsResult = {
	optionsRecentProjects: JiraProjects;
	optionsAllProjects: JiraProjects;
	selectedProject: JiraProject | null;
	isLoading: boolean;
	error: Error | null;
	onProjectChange: (project: JiraProject | null) => void;
};

export const useGetJiraProjectsForSingleIssueCreateForm = (
	selectedAvailableSite: AvailableSite | null = null,
	projectSelectorQuery: string | null = null,
): UseGetJiraProjectsResult => {
	const [optionsRecentProjects, setOptionsRecentProjects] = useState<JiraProjects>([]);
	const [optionsAllProjects, setOptionsAllProjects] = useState<JiraProjects>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [selectedProject, setSelectedProject] = useState<JiraProject | null>(null);

	const resetState = useCallback(() => {
		setOptionsRecentProjects([]);
		setOptionsAllProjects([]);
		setSelectedProject(null);
		setError(null);
	}, []);

	// Reset state when site changes
	useEffect(() => {
		resetState();
	}, [selectedAvailableSite?.cloudId, resetState]);

	// Fetch projects when site is available
	useEffect(() => {
		if (!selectedAvailableSite?.cloudId) {
			return;
		}

		const cloudId = selectedAvailableSite.cloudId;
		const query = projectSelectorQuery?.trim() || '';

		const fetchProjects = async () => {
			setIsLoading(true);
			setError(null);

			try {
				if (query) {
					// If there's a search query, only fetch filtered projects
					const searchResults = await fetchJiraProjects({ cloudId, orderBy: 'name', query });
					setOptionsRecentProjects([]);
					setOptionsAllProjects(searchResults || []);

					// Auto-select first search result
					if (searchResults && searchResults.length > 0) {
						setSelectedProject(searchResults[0]);
					}
				} else {
					// Otherwise fetch both recent and all projects
					const [recentProjectsResult, allProjectsResult] = await Promise.allSettled([
						fetchRecentJiraProjects({ cloudId }),
						fetchJiraProjects({ cloudId, orderBy: 'name' }),
					]);

					let recentProjectsWithPermissions: JiraProjects = [];

					// Handle recent projects
					if (recentProjectsResult.status === 'fulfilled' && recentProjectsResult.value) {
						const recentProjectIds = recentProjectsResult.value.map((project) => project.id);
						if (recentProjectIds.length > 0) {
							recentProjectsWithPermissions =
								(await fetchJiraProjects({
									cloudId,
									orderBy: 'name',
									projectIds: recentProjectIds,
								})) || [];
							setOptionsRecentProjects(recentProjectsWithPermissions);
						}
					}

					// Handle all projects
					if (allProjectsResult.status === 'fulfilled' && allProjectsResult.value) {
						const allProjects = allProjectsResult.value;
						setOptionsAllProjects(allProjects);

						if (recentProjectsWithPermissions.length > 0) {
							setSelectedProject(recentProjectsWithPermissions[0]);
						} else if (allProjects.length > 0) {
							setSelectedProject(allProjects[0]);
						}
					}
				}
			} catch (e: Error | unknown) {
				setError(e as Error);
				resetState();
			} finally {
				setIsLoading(false);
			}
		};

		void fetchProjects();
	}, [selectedAvailableSite?.cloudId, projectSelectorQuery, resetState]);

	const handleProjectChange = useCallback((project: JiraProject | null) => {
		setSelectedProject(project);
	}, []);

	return {
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject,
		isLoading,
		error,
		onProjectChange: handleProjectChange,
	};
};
